<template>
  <div class="form-container">
    <h1 class="page-title">顧客マスタ情報編集</h1>
    <form @submit.prevent="handleSubmit" @keydown.enter.prevent>
      <div class="mb-3">
        <label for="id" class="form-label">ID：</label>
        {{ customer.id}}
      </div>

      <div class="mb-3">
        <label for="customerName" class="form-label">顧客名 <span class="badge text-bg-danger">必須</span></label>
        <textarea
          v-model="customer.customer_nm"
          placeholder="顧客名"
          class="form-control"
          id="customerName"
          required
          rows="2"
          @keydown.enter.stop
        ></textarea>
        <div v-if="errorMessages.customer_nm" class="alert alert-danger mt-2">{{ errorMessages.customer_nm[0] }}</div>
      </div>

      <div class="mb-3">
        <label for="customerNo" class="form-label">顧客番号</label>
        <input v-model="customer.customer_no" type="text" placeholder="顧客番号" class="form-control" id="customerNo">
        <div v-if="errorMessages.customer_no" class="alert alert-danger mt-2">{{ errorMessages.customer_no[0] }}</div>
      </div>

      <div class="mb-3">
        <label class="form-label">所属する本社</label>
        <CustomerSelectBox
          v-if="!hasBranches"
          v-model="customer.parent_customer_id"
          :options="parentCustomerOptions"
          placeholder="支店として登録する場合のみ、本社名またはIDで検索"
        />
        <div v-else class="form-control-plaintext">
          未設定（この顧客には削除済みを含む支店が紐づいているため、本社として管理されています）
        </div>
        <div class="form-text">
          変更すると、過去分を含む売上の「本社別（支店含む）」集計へ直ちに反映されます。請求先には影響しません。
        </div>
        <div v-if="errorMessages.parent_customer_id" class="alert alert-danger mt-2">{{ errorMessages.parent_customer_id[0] }}</div>
      </div>

      <PostalCodeInput
        v-model="customer.postal_code"
        @address="({ region, locality, street }) => {
          customer.prefecture = region;
          customer.city = locality;
          customer.other_address = street;
        }"
      />

      <div class="mb-3">
        <label for="prefecture" class="form-label">都道府県</label>
        <input v-model="customer.prefecture" type="text" placeholder="都道府県" class="form-control" id="prefecture">
        <div v-if="errorMessages.prefecture" class="alert alert-danger mt-2">{{ errorMessages.prefecture[0] }}</div>
      </div>

      <div class="mb-3">
        <label for="city" class="form-label">市区町村</label>
        <input v-model="customer.city" type="text" placeholder="市区町村" class="form-control" id="city">
        <div v-if="errorMessages.city" class="alert alert-danger mt-2">{{ errorMessages.city[0] }}</div>
      </div>

      <div class="mb-3">
        <label for="otherAddress" class="form-label">その他の住所</label>
        <input v-model="customer.other_address" type="text" placeholder="その他の住所" class="form-control" id="otherAddress">
        <div v-if="errorMessages.other_address" class="alert alert-danger mt-2">{{ errorMessages.other_address[0] }}</div>
      </div>

      <div class="mb-3">
        <label for="telNum" class="form-label">電話番号</label>
        <input v-model="customer.tel_num" type="text" placeholder="電話番号" class="form-control" id="telNum">
        <div v-if="errorMessages.tel_num" class="alert alert-danger mt-2">{{ errorMessages.tel_num[0] }}</div>
      </div>

      <div class="mb-3">
        <label for="fax" class="form-label">FAX</label>
        <input v-model="customer.fax" type="text" placeholder="FAX" class="form-control" id="fax">
        <div v-if="errorMessages.fax" class="alert alert-danger mt-2">{{ errorMessages.fax[0] }}</div>
      </div>

      <div class="mb-3">
        <label for="presidentNm" class="form-label">代表者名</label>
        <input v-model="customer.president_nm" type="text" placeholder="代表者名" class="form-control" id="presidentNm">
        <div v-if="errorMessages.president_nm" class="alert alert-danger mt-2">{{ errorMessages.president_nm[0] }}</div>
      </div>

      <div class="mb-3">
        <label for="billingTimingType" class="form-label">入金予定</label>
        <select class="form-select" v-model="customer.billing_timing_type">
          <option value="">選択してください</option>
          <option value="0">翌月15日</option>
          <option value="1">翌月25日</option>
          <option value="2">翌月末</option>
          <option value="3">翌々月5日</option>
          <option value="4">翌々月10日</option>
        </select>
      </div>

      <div class="mb-3">
        <label for="billingDeadline" class="form-label">請求締切</label>
        <select class="form-select" v-model="customer.billing_deadline">
          <option value="">選択してください</option>
          <option value="0">5日</option>
          <option value="1">10日</option>
          <option value="2">15日</option>
          <option value="3">20日</option>
          <option value="4">25日</option>
          <option value="5">月末</option>
        </select>
      </div>

      <div class="mb-3">
        <label class="form-label">振込先銀行</label>
        <div v-if="banks.length === 0" class="text-muted">銀行マスタが未登録です。</div>
        <div v-else>
          <div
            v-for="bank in banks"
            :key="bank.id"
            class="form-check mb-1"
          >
            <label class="ECM_CheckboxInput-sm" :for="`bank_${bank.id}`">
              <input
                class="ECM_CheckboxInput-Input-sm"
                type="checkbox"
                :id="`bank_${bank.id}`"
                :value="bank.id"
                v-model="selectedBankIds"
              >
              <span class="ECM_CheckboxInput-DummyInput-sm me-2"></span>
              {{ bank.bank_nm }} {{ bank.branch_nm }} {{ bank.account_type }} {{ bank.account_no }}
            </label>
          </div>
        </div>
      </div>

      <div class="btn-area">
        <a class="btn btn-secondary mx-1" @click="$router.back()">戻る</a>
        <button type="submit" class="btn btn-success mx-1">更新</button>
      </div>
    </form>
  </div>
</template>

<script>
import { computed, ref, onMounted, watchEffect } from 'vue';
import { useStore } from 'vuex';
import { useRoute, useRouter } from 'vue-router';
import {
  getCustomer,
  getSelectBoxBanks,
  getSelectBoxCustomers,
  updateCustomer
} from '@/api/axios';
import PostalCodeInput from '@/components/shared/PostalCodeInput.vue';
import CustomerSelectBox from '@/components/searchSelectBoxes/CustomerSelectBox.vue';

export default {
  components: {
    PostalCodeInput,
    CustomerSelectBox
  },
  setup() {
    const title = ref('顧客情報編集');

    watchEffect(() => {
      document.title = title.value;
    });

    const customer = ref({
      customer_nm: '',
      customer_no: '',
      parent_customer_id: null,
      postal_code: '',
      prefecture: '',
      city: '',
      other_address: '',
      tel_num: '',
      fax: '',
      president_nm: '',
      billing_nm: '',
      billing_timing_type: '',
      billing_deadline: ''
      // 他のフィールド
    });

    const banks = ref([]);
    const customers = ref([]);
    const selectedBankIds = ref([]);

    // エラーメッセージ用のリアクティブプロパティ
    const errorMessages = ref({});

    const store = useStore();
    const router = useRouter();
    const route = useRoute();
    const customerId = route.params.id; // URLから顧客情報IDを取得
    const hasBranches = computed(() => {
      return (customer.value.branch_customers || []).length > 0;
    });
    const parentCustomerOptions = computed(() => {
      return customers.value.filter((candidate) => {
        return candidate.parent_customer_id == null && String(candidate.id) !== String(customerId);
      });
    });

    const loadCustomerData = async () => {
      try {
        const response = await getCustomer(customerId);
        customer.value = response.data;
        selectedBankIds.value = (response.data.m_banks || []).map((bank) => bank.id);
      } catch (error) {
        console.error('顧客情報データの取得に失敗しました:', error);
      }
    };

    const handleSubmit = async () => {
      try {
        customer.value.m_bank_ids = selectedBankIds.value;
        await updateCustomer(customerId, customer.value);
        store.dispatch('setFlashMessage', {message: '顧客情報更新完了！', type: 'success'});
        router.push({
          path: `/customers/${customerId}`,
          query: route.query
        });
      } catch (error) {
        if (error.response && error.response.data) {
          // エラーメッセージをerrorMessagesにセット
          errorMessages.value = error.response.data || {};
        }
        store.dispatch('setFlashMessage', {message: '顧客情報更新失敗！', type: 'danger'});
        console.error('更新エラー:', error);
      }
    };

    const fetchBanks = async () => {
      try {
        const response = await getSelectBoxBanks();
        banks.value = response.data || [];
      } catch (error) {
        console.error('銀行マスタの取得に失敗しました:', error);
      }
    };

    const fetchCustomers = async () => {
      try {
        const response = await getSelectBoxCustomers();
        customers.value = response.data || [];
      } catch (error) {
        console.error('親顧客候補の取得に失敗しました:', error);
      }
    };

    onMounted(() => {
      fetchBanks();
      fetchCustomers();
      loadCustomerData();
    });

    return {
      title,
      customer,
      handleSubmit,
      errorMessages,
      banks,
      hasBranches,
      parentCustomerOptions,
      selectedBankIds
    };
  }
};
</script>
